import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { invoiceService, ticketService } from '../services';
import { getMyBalance } from '../services/creditService';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  FileText, CheckCircle, Clock, TrendingUp, Ticket, AlertTriangle, Loader2, Euro, Coins
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, subtitle, colorClass }) => (
  <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
    className="hover:shadow-lg transition-shadow duration-200"
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{title}</CardTitle>
      <div className={`p-2 rounded-lg ${colorClass}`}>
        <Icon className="w-4 h-4" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
        {value}
      </div>
      {subtitle && <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{subtitle}</p>}
    </CardContent>
  </Card>
);

const formatCurrency = (amount) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try { return new Date(dateStr).toLocaleDateString('de-DE'); }
  catch { return dateStr; }
};

const statusBadge = (status) => {
  const map = {
    paid: { label: 'Bezahlt', cls: 'bg-green-50 text-green-700 border-green-200' },
    sent: { label: 'Versendet', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    draft: { label: 'Entwurf', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
    overdue: { label: 'Überfällig', cls: 'bg-red-50 text-red-700 border-red-200' },
    partial: { label: 'Teilbezahlt', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    open: { label: 'Offen', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    pending: { label: 'In Bearbeitung', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
    closed: { label: 'Geschlossen', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  };
  const c = map[status] || { label: status, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return <Badge className={`${c.cls} border text-xs`}>{c.label}</Badge>;
};

const priorityBadge = (priority) => {
  const map = {
    high: { label: 'Hoch', cls: 'bg-red-50 text-red-700 border-red-200' },
    medium: { label: 'Mittel', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    low: { label: 'Niedrig', cls: 'bg-green-50 text-green-700 border-green-200' },
  };
  const c = map[priority] || map.medium;
  return <Badge className={`${c.cls} border text-xs`}>{c.label}</Badge>;
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [creditBalance, setCreditBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsData, invoicesData, ticketsData, balanceData] = await Promise.all([
          invoiceService.getDashboardStats().catch(() => null),
          invoiceService.getInvoices({ limit: 5 }).catch(() => ({ invoices: [] })),
          ticketService.getTickets({ limit: 5 }).catch(() => ({ tickets: [] })),
          getMyBalance().catch(() => null),
        ]);
        setStats(statsData);
        setRecentInvoices(invoicesData?.invoices || []);
        setRecentTickets(ticketsData?.tickets || []);
        setCreditBalance(balanceData);
        setRecentTickets(ticketsData?.tickets || []);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-primary)' }} />
      </div>
    );
  }

  const paidRate = stats?.total_invoices
    ? Math.round((stats.paid_invoices / stats.total_invoices) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="dashboard-page"
    >
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
          Dashboard
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Willkommen zurück! Hier ist eine Übersicht Ihrer wichtigsten Kennzahlen.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
        <StatCard
          title="Gesamt Rechnungen"
          value={stats?.total_invoices || 0}
          icon={FileText}
          subtitle={stats?.source === 'zoho_books' ? 'Aus Zoho Books' : null}
          colorClass="bg-blue-50 text-blue-600"
        />
        <StatCard
          title="Bezahlt"
          value={stats?.paid_invoices || 0}
          icon={CheckCircle}
          subtitle={`${paidRate}% Zahlungsquote`}
          colorClass="bg-green-50 text-green-600"
        />
        <StatCard
          title="Ausstehend"
          value={stats?.pending_invoices || 0}
          icon={Clock}
          subtitle={stats?.overdue_invoices ? `${stats.overdue_invoices} überfällig` : null}
          colorClass="bg-orange-50 text-orange-600"
        />
        <StatCard
          title="Offener Betrag"
          value={formatCurrency(stats?.total_outstanding)}
          icon={Euro}
          subtitle={stats?.total_revenue ? `${formatCurrency(stats.total_revenue)} Umsatz` : null}
          colorClass="bg-blue-50 text-blue-600"
        />
        <div 
          onClick={() => navigate('/credits')} 
          className="cursor-pointer"
          data-testid="credit-balance-widget"
        >
          <StatCard
            title="Credits"
            value={creditBalance?.current_balance?.toFixed(2) || '0.00'}
            icon={Coins}
            subtitle={creditBalance?.transaction_count ? `${creditBalance.transaction_count} Transaktionen` : 'Klicken für Details'}
            colorClass="bg-purple-50 text-purple-600"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Recent Invoices */}
        <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }} data-testid="recent-invoices-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
              Letzte Rechnungen
            </CardTitle>
            <button onClick={() => navigate('/invoices')} className="text-xs hover:underline" style={{ color: 'var(--color-primary)' }}>
              Alle anzeigen
            </button>
          </CardHeader>
          <CardContent>
            {recentInvoices.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-secondary)' }}>Keine Rechnungen vorhanden</p>
            ) : (
              <div className="space-y-3">
                {recentInvoices.map((inv) => (
                  <div key={inv.invoice_id} className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                    style={{ backgroundColor: 'var(--color-background)' }}
                    onClick={() => navigate('/invoices')}
                    data-testid={`dashboard-invoice-${inv.invoice_number}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{inv.invoice_number}</p>
                        <p className="text-sm truncate" style={{ color: 'var(--color-text-secondary)' }}>{inv.customer_name}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(inv.total)}</p>
                      {statusBadge(inv.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Tickets */}
        <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }} data-testid="recent-tickets-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
              Aktive Tickets
            </CardTitle>
            <button onClick={() => navigate('/tickets')} className="text-xs hover:underline" style={{ color: 'var(--color-primary)' }}>
              Alle anzeigen
            </button>
          </CardHeader>
          <CardContent>
            {recentTickets.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-secondary)' }}>Keine aktiven Tickets</p>
            ) : (
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <div key={ticket.ticket_id} className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                    style={{ backgroundColor: 'var(--color-background)' }}
                    onClick={() => navigate('/tickets')}
                    data-testid={`dashboard-ticket-${ticket.ticket_number || ticket.ticket_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Ticket className="w-5 h-5 text-blue-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{ticket.subject}</p>
                        <p className="text-sm truncate" style={{ color: 'var(--color-text-secondary)' }}>
                          {ticket.ticket_number} {ticket.department ? `· ${ticket.department}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      {priorityBadge(ticket.priority)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
};
